/**
 * A dynamic storage wrapper that implements the StorageLike interface.
 *
 * This class provides a prefixed storage abstraction over the browser's native Storage API
 * (localStorage or sessionStorage). It allows for flexible switching between storage types
 * while maintaining data migration capabilities.
 *
 * @remarks
 * - All keys are automatically prefixed to namespace the stored data.
 * - The `clear()` method only removes items with the current prefix, not all storage items.
 * - Storage can be switched at runtime with automatic data migration via `switchStorage()`.
 *
 * @example
 * ```typescript
 * // Create a prefixed localStorage instance
 * const storage = new DynamicStorage('myapp', StorageType.LocalStorage);
 * storage.setItem('user', 'john');
 *
 * // Switch to sessionStorage and migrate data
 * storage.switchStorage(StorageType.SessionStorage);
 * ```
 */
import type { StorageLike } from '@vueuse/core';

/**
 * Enum representing the available storage types.
 */
export enum StorageType {
  LocalStorage = 'localStorage',
  SessionStorage = 'sessionStorage',
}

/**
 * A class that provides a dynamic wrapper for localStorage and sessionStorage.
 */
export class DynamicStorage implements StorageLike {
  /**
   * The current storage instance (either localStorage or sessionStorage).
   */
  private storage: Storage;

  /**
   * The prefix used to namespace all keys stored by this instance.
   */
  private readonly prefix: string;

  /**
   * Creates a new DynamicStorage instance.
   *
   * @param prefix - A string used to namespace all keys stored by this instance.
   * @param storageType - The initial storage type (localStorage or sessionStorage).
   */
  constructor(prefix: string, storageType: StorageType = StorageType.LocalStorage) {
    this.storage = this.getStorageOfType(storageType);
    this.prefix = `${prefix}:`;
  }

  /**
   * Generates a fully qualified key by adding the prefix.
   *
   * @param key - The key to be prefixed.
   * @returns The prefixed key.
   */
  private getKey(key: string): string {
    return `${this.prefix}${key}`;
  }

  /**
   * Retrieves an item from storage.
   *
   * @param key - The key of the item to retrieve.
   * @returns The value associated with the key, or `null` if the key does not exist.
   */
  getItem(key: string): string | null {
    return this.storage.getItem(this.getKey(key));
  }

  /**
   * Stores an item in storage.
   *
   * @param key - The key under which the value will be stored.
   * @param value - The value to store.
   */
  setItem(key: string, value: string): void {
    this.storage.setItem(this.getKey(key), value);
  }

  /**
   * Removes an item from storage.
   *
   * @param key - The key of the item to remove.
   */
  removeItem(key: string): void {
    this.storage.removeItem(this.getKey(key));
  }

  /**
   * Clears all items in storage that match the current prefix.
   */
  clear(): void {
    // Only clear items with the prefix
    Object.keys(this.storage).forEach((key) => {
      if (key.startsWith(this.prefix)) {
        this.storage.removeItem(key);
      }
    });
  }

  /**
   * Resolves the storage type to the corresponding browser storage instance.
   *
   * @param storageType - The storage type to resolve (localStorage or sessionStorage).
   * @returns The corresponding Storage instance.
   */
  private getStorageOfType(storageType: StorageType = StorageType.LocalStorage): Storage {
    switch (storageType) {
      case StorageType.SessionStorage:
        return sessionStorage;
      default:
        return localStorage;
    }
  }

  /**
   * Switches the storage type and migrates all data with the current prefix to the new storage.
   *
   * @param storageType - The new storage type to switch to (localStorage or sessionStorage).
   */
  switchStorage(storageType: StorageType): void {
    const currentData: Record<string, string> = {};

    // Migrate data from the current storage to the new one
    for (let i = 0; i < this.storage.length; i++) {
      const key = this.storage.key(i);
      if (key && key.startsWith(this.prefix)) {
        currentData[key] = this.storage.getItem(key) as string;
      }
    }

    this.storage = this.getStorageOfType(storageType);

    // Populate the new storage with the prefixed data
    Object.entries(currentData).forEach(([key, value]) => {
      this.storage.setItem(key, value);
    });
  }
}

/**
 * Resolves a storage type from a string value, falling back to a default if the value is invalid.
 *
 * @param v - The string value to resolve.
 * @param def - The default storage type to use if the value is invalid.
 * @returns The resolved storage type.
 */
export function resolveStorageType(v: string | undefined | null, def: StorageType): StorageType {
  switch (v) {
    case StorageType.LocalStorage:
      return StorageType.LocalStorage;
    case StorageType.SessionStorage:
      return StorageType.SessionStorage;
    default:
      return def;
  }
}
